import React from 'react';
import { motion } from 'framer-motion';
import { beginnerRigs } from '../data/rigsData';
import { Guitar, Headphones, DollarSign, ArrowRight, Music2 } from 'lucide-react';

const ToneFinder = () => {
  return (
    <section id="tonefinder" className="relative py-24 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-950/80"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="h-2 w-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 mr-2"></span>
            <span className="text-sm font-medium bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
              Tone Matching Made Easy
            </span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Find Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">Signature Sound</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Discover the perfect gear combinations for your favorite genres and playing styles.
          </p>
        </motion.div>

        {/* Rig cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {beginnerRigs.map((rig, index) => (
            <motion.div
              key={rig.id}
              className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl rounded-2xl p-0.5 overflow-hidden hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="relative h-full bg-gradient-to-br from-gray-900 to-gray-800/80 rounded-2xl overflow-hidden">
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Music2 className="h-16 w-16 text-blue-400 opacity-30" />
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {rig.genre}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">
                    {rig.name}
                  </h3>
                  
                  <p className="text-gray-300 text-sm mb-4">
                    {rig.description}
                  </p>

                  {/* Gear info */}
                  <div className="space-y-3 mb-5">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-lg bg-white/5">
                        <Guitar className="h-4 w-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Guitar</p>
                        <p className="text-white">{rig.guitar}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-lg bg-white/5">
                        <Headphones className="h-4 w-4 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Amp</p>
                        <p className="text-white">{rig.amp}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-lg bg-white/5">
                        <DollarSign className="h-4 w-4 text-green-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Price</p>
                        <p className="text-white">{rig.priceRange}</p>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {rig.features.map((feature, idx) => (
                        <span 
                          key={idx}
                          className="bg-white/5 text-gray-300 px-3 py-1 rounded-full text-xs border border-white/10"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <motion.button
                    className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium flex items-center justify-center gap-2 group"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>View Details</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div 
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <p className="text-lg text-gray-300 mb-6">
            Can't find what you're looking for?
          </p>
          <motion.button
            className="px-6 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-colors flex items-center gap-2 mx-auto"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <Headphones className="h-5 w-5" />
            Get Personalized Recommendations
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default ToneFinder;
